import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useLocaleStore } from '../Contexts/LocaleContext'

import useShowEmergency from '../../hooks/useShowEmergency'
import useShowMap from '../../hooks/useShowMap'

import type { CustomMenu } from '../../models/types'
import { CurrentSubView, RetorikEvent } from '../../models/enums'

const HistoryList = lazy(() => import('../History/HistoryList'))
const LanguageChoice = lazy(() => import('../Language/LanguageChoice'))
const AttachmentDetails = lazy(() => import('../Attachments/AttachmentDetail'))
const FullScreenImage = lazy(() => import('../Attachments/Utils/FullScreenImage'))
const Weather = lazy(() => import('../Weather/Weather'))
const CustomView = lazy(() => import('./CustomView'))
const Emergency = lazy(() => import('../Emergency/Emergency'))

const SubViews = ({ externalComponents }): JSX.Element => {
  const locale = useLocaleStore((state) => state.locale)
  const currentSubView = useUtilsStore((state) => state.currentSubView)
  const currentCustomView = useUtilsStore((state) => state.currentCustomView)
  const enabledFeatures = useRetorikStore((state) => state.enabledFeatures)
  const { setCurrentCustomView, setCurrentSubView, setRetorikEvent, unlockClickedButton } = useUtilsStore((state) => state.actions)
  const configuration = useRetorikStore((state) => state.configuration)

  const showMap = useShowMap()
  const showEmergency = useShowEmergency()

  const [displayCustomComponent, setDisplayCustomComponent] = useState<boolean>(true)
  const [customView, setCustomView] = useState<{ view: string; title: string; icon?: JSX.Element } | undefined>(undefined)

  useEffect(() => {
    if (currentCustomView) {
      let label = ''
      if (currentCustomView.label && typeof currentCustomView.label === 'string') {
        label = currentCustomView.label
      } else if (currentCustomView.label && typeof currentCustomView.label === 'object') {
        label = currentCustomView.label[locale]
      }
      if (label && currentCustomView.customDisplay && currentCustomView.icon) {
        setCustomView({
          view: currentCustomView.customDisplay,
          title: label,
          icon: currentCustomView.icon
        })
      }
      setCurrentSubView(CurrentSubView.custom)
    }
  }, [currentCustomView])

  useEffect(() => {
    if (showMap && currentSubView !== CurrentSubView.custom) {
      if (externalComponents && externalComponents.length > 0) {
        const component = (externalComponents as Array<{ name: string; component: JSX.Element }>)?.find((e) => e.name === 'mapDisplay')
        if (component && component.component) {
          if (configuration.baseMenu || configuration.customMenu) {
            let allMenus: Array<CustomMenu> = []
            configuration.baseMenu &&
              // Filter on 'icon' key to get only CustomMenu items
              (allMenus = [...configuration.baseMenu].filter((m) => Object.keys(m).includes('icon')) as CustomMenu[])
            if (configuration.customMenu && configuration.customMenu.length > 0) {
              if (Array.isArray(configuration.customMenu[0])) {
                const tempCustomMenu = configuration.customMenu as [CustomMenu[]]
                tempCustomMenu.forEach((menu: CustomMenu[]) => {
                  allMenus = [...allMenus, ...menu]
                })
              } else {
                allMenus = [...allMenus, ...(configuration.customMenu as CustomMenu[])]
              }
            }

            const customMenuMap = allMenus.find((c) => c.customDisplay && c.customDisplay === 'mapDisplay')
            if (customMenuMap) {
              if (customView && customView.view === customMenuMap.customDisplay) {
              } else {
                setCurrentCustomView(customMenuMap)
              }
              setRetorikEvent(RetorikEvent.DetailViewOpen)
              setCurrentSubView(CurrentSubView.custom)
              setDisplayCustomComponent(true)
            }
          }
        }
      }
    }
  }, [showMap])

  useEffect(() => {
    if (showEmergency) {
      setCurrentSubView(CurrentSubView.emergency)
    }
  }, [showEmergency])

  useEffect(() => {
    unlockClickedButton()
    // Special processing for external / custom data
    setDisplayCustomComponent(currentSubView === CurrentSubView.custom)
    setRetorikEvent(currentSubView === CurrentSubView.none ? RetorikEvent.DetailViewClose : RetorikEvent.DetailViewOpen)
  }, [currentSubView])

  const processedSubView = useMemo<CurrentSubView>(() => {
    switch (currentSubView) {
      case CurrentSubView.none:
      case CurrentSubView.history:
      case CurrentSubView.languages:
      case CurrentSubView.custom:
        return currentSubView
      case CurrentSubView.emergency:
        if (enabledFeatures.includes('emergency')) {
          return CurrentSubView.emergency
        } else {
          console.log('Retorik Framework > tried to switch to emergency while the feature is not activated in Retorik Studio')
        }
        break
      case CurrentSubView.weather:
        if (enabledFeatures.includes('weather')) {
          return CurrentSubView.weather
        } else {
          console.log('Retorik Framework > tried to switch to weather while the feature is not activated in Retorik Studio')
        }
        break
    }

    return CurrentSubView.none
  }, [currentSubView, enabledFeatures])

  return (
    <Suspense>
      {processedSubView === CurrentSubView.history ? (
        <HistoryList />
      ) : processedSubView === CurrentSubView.languages ? (
        <LanguageChoice />
      ) : processedSubView === CurrentSubView.weather ? (
        <Weather />
      ) : processedSubView === CurrentSubView.emergency ? (
        <Emergency />
      ) : (
        <React.Fragment />
      )}

      <CustomView
        display={displayCustomComponent}
        externalComponents={externalComponents}
        customView={customView?.view}
        title={customView?.title}
        icon={customView?.icon}
        setDisplayCustomComponent={setDisplayCustomComponent}
      />

      <AttachmentDetails />
      <FullScreenImage />
    </Suspense>
  )
}

export default SubViews
